import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ScrollReveal = ({
  children,
  direction = "up",
  delay = 0,
  duration = 0.8,
  distance = 60,
  stagger = 0,
  start = "top 85%",
  once = true,
  className = "",
  as: Tag = "div",
  variant = "fade", // fade, scale, clip
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fromVars = { opacity: 0 };
    const toVars = {
      opacity: 1,
      duration,
      delay,
      ease: "power3.out",
      overwrite: "auto",
    };

    if (variant === "fade" || variant === "scale") {
      switch (direction) {
        case "up":
          fromVars.y = distance;
          toVars.y = 0;
          break;
        case "down":
          fromVars.y = -distance;
          toVars.y = 0;
          break;
        case "left":
          fromVars.x = distance;
          toVars.x = 0;
          break;
        case "right":
          fromVars.x = -distance;
          toVars.x = 0;
          break;
        default:
          break;
      }
    }

    if (variant === "scale") {
      fromVars.scale = 0.8;
      toVars.scale = 1;
    } else if (variant === "clip") {
      fromVars.clipPath = "inset(100% 0 0 0)";
      fromVars.opacity = 1; 
      fromVars.y = 0;
      toVars.clipPath = "inset(0% 0% 0% 0%)";
    }

    const ctx = gsap.context(() => {
      if (stagger > 0) {
        const targets = el.children;
        gsap.set(targets, fromVars);
        ScrollTrigger.create({
          trigger: el,
          start,
          toggleActions: once ? "play none none none" : "play none none reverse",
          onEnter: () => {
            gsap.to(targets, {
              ...toVars,
              stagger,
            });
          },
        });
      } else {
        gsap.set(el, fromVars);
        ScrollTrigger.create({
          trigger: el,
          start,
          toggleActions: once ? "play none none none" : "play none none reverse",
          onEnter: () => {
            gsap.to(el, toVars);
          },
        });
      }
    });

    return () => ctx.revert();
  }, [direction, delay, duration, distance, stagger, start, once, variant]);

  return (
    <Tag ref={ref} className={className}>
      {children}
    </Tag>
  );
};

export default ScrollReveal;
